import React from 'react';

import { FormattedMessage } from 'react-intl';

import EditorInfoIcon from '@atlaskit/icon/glyph/info';
import Lozenge from '@atlaskit/lozenge';
import { Anchor, Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import {
  DEFAULT_CODE_REVIEWER_COMMENT_UUIDS,
  identifyCodeReviewerComment,
} from 'src/components/conversation/src/utils/code-reviewer-comment-identifiers';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import { useIntl } from 'src/hooks/intl';

import messages from '../i18n/code-reviewer-feedback.i18n';

export const CodeReviewerFooter = ({
  createdBy,
}: {
  createdBy: FabricUser;
}): React.ReactElement => {
  const { formatMessage } = useIntl();
  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );
  const isCodeReviewerComment = identifyCodeReviewerComment(
    createdBy,
    commentUUIDs
  );

  const isCodeReviewerFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  if (!isCodeReviewerComment || !isCodeReviewerFeedbackEnabled) {
    return <></>;
  }

  const anchorStyles = xcss({
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      textDecoration: 'none',
    },
  });

  // this resize balances this info icon with the Rovo icon on the other side
  const infoIconContainerStyles = xcss({
    transform: 'scale(0.8)',
    paddingTop: 'space.025',
  });

  return (
    <Box paddingBlockStart="space.200" paddingInlineEnd="space.200">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap="space.050">
          <Lozenge>
            <FormattedMessage {...messages.beta} />
          </Lozenge>
          <Box xcss={infoIconContainerStyles}>
            <EditorInfoIcon
              label={formatMessage(messages.aiUsageDisclaimer)}
              primaryColor={token('color.icon.subtle')}
              size="small"
            />
          </Box>
          <Anchor
            href="https://www.atlassian.com/trust/atlassian-intelligence"
            target="_blank"
            xcss={anchorStyles}
          >
            <Text size="small" color="color.text.subtlest">
              <FormattedMessage {...messages.aiUsageDisclaimer} />
            </Text>
          </Anchor>
        </Flex>
      </Flex>
    </Box>
  );
};
